import { FC, useState } from "react"
import {
  LeadworkflowDetails,
  LeadworkflowStep,
  LeadworkflowStepItem,
  LeadworkflowStepType
} from "../../../../../../../../types"
import { getLeadworkflowStep } from "../../../../../../../utils"
import BBSwitch from "../../../../../../form/Switch"

interface Props {
  values: LeadworkflowDetails
  setConfigField: (field: string, value: any) => void
}

// ─── Step type metadata ────────────────────────────────────────────────────────

const STEP_META: Record<
  LeadworkflowStepType,
  { label: string; color: string }
> = {
  welcome: {
    label: "Welcome",
    color: "bg-violet-50 text-violet-600 border-violet-100"
  },
  buttons: {
    label: "Buttons",
    color: "bg-blue-50 text-blue-600 border-blue-100"
  },
  list: { label: "List", color: "bg-sky-50 text-sky-600 border-sky-100" },
  slider: {
    label: "Slider",
    color: "bg-amber-50 text-amber-600 border-amber-100"
  },
  location_autocomplete: {
    label: "Location",
    color: "bg-emerald-50 text-emerald-600 border-emerald-100"
  },
  lo_select: {
    label: "LO Select",
    color: "bg-rose-50 text-rose-600 border-rose-100"
  }
}

const STEP_TYPES = Object.keys(STEP_META) as LeadworkflowStepType[]

// ─── Small shared primitives ───────────────────────────────────────────────────

interface FieldProps {
  label: string
  children: React.ReactNode
}

const Field: FC<FieldProps> = ({ label, children }) => (
  <div>
    <label className="block text-xs font-medium text-gray-600 mb-1">
      {label}
    </label>
    {children}
  </div>
)

const TextInput: FC<{
  value: string
  onChange: (v: string) => void
  placeholder?: string
}> = ({ value, onChange, placeholder }) => (
  <input
    type="text"
    value={value}
    onChange={e => onChange(e.target.value)}
    placeholder={placeholder}
    className="w-full px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-md text-gray-700 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 transition-colors"
  />
)

const NumberInput: FC<{
  label: string
  value: number | undefined
  onChange: (v: number) => void
  placeholder?: string
}> = ({ label, value, onChange, placeholder }) => (
  <Field label={label}>
    <input
      type="number"
      value={value ?? ""}
      onChange={e => onChange(Number(e.target.value))}
      placeholder={placeholder}
      className="w-full px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-md text-gray-700 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 transition-colors"
    />
  </Field>
)

// ─── Item row (label / value / icon) ──────────────────────────────────────────

interface ItemRowProps {
  item: LeadworkflowStepItem
  onUpdate: (updated: LeadworkflowStepItem) => void
  onRemove: () => void
}

const ItemRow: FC<ItemRowProps> = ({ item, onUpdate, onRemove }) => (
  <div className="group flex items-center gap-2 p-2.5 rounded-xl bg-white border border-gray-100 hover:border-gray-200 hover:shadow-sm transition-all">
    <div className="flex-1 grid grid-cols-2 gap-2">
      <input
        value={item.label}
        onChange={e => onUpdate({ ...item, label: e.target.value })}
        placeholder="Label"
        className="text-xs px-2.5 py-1.5 bg-gray-50 border border-gray-100 rounded-md text-gray-700 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
      />
      <input
        value={item.value}
        onChange={e => onUpdate({ ...item, value: e.target.value })}
        placeholder="Value"
        className="text-xs px-2.5 py-1.5 bg-gray-50 border border-gray-100 rounded-md text-gray-600 font-mono focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
      />
    </div>
    <div className="flex-1">
      <input
        value={item.icon ?? ""}
        onChange={e => onUpdate({ ...item, icon: e.target.value })}
        placeholder="Icon URL (optional)"
        className="w-full text-xs px-2.5 py-1.5 bg-gray-50 border border-gray-100 rounded-md text-gray-600 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
      />
    </div>
    <button
      onClick={onRemove}
      className="opacity-0 group-hover:opacity-100 w-6 h-6 flex items-center justify-center rounded-lg hover:bg-red-50 text-gray-300 hover:text-red-400 transition-all text-lg leading-none"
    >
      ×
    </button>
  </div>
)

// ─── Items editor block ────────────────────────────────────────────────────────

const ItemsEditor: FC<{
  items: LeadworkflowStepItem[]
  onChange: (items: LeadworkflowStepItem[]) => void
}> = ({ items, onChange }) => {
  const addItem = () => onChange([...items, { label: "", value: "" }])
  const updateItem = (i: number, updated: LeadworkflowStepItem) => {
    const next = [...items]
    next[i] = updated
    onChange(next)
  }
  const removeItem = (i: number) =>
    onChange(items.filter((_, idx) => idx !== i))

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm font-semibold text-gray-700">Options</p>
        <span className="text-xs text-gray-400">{items.length} items</span>
      </div>
      {items.length === 0 && (
        <p className="text-xs text-gray-400 text-center py-3 bg-gray-50 rounded-xl">
          No options yet
        </p>
      )}
      <div className="space-y-1.5">
        {items.map((item, i) => (
          <ItemRow
            key={i}
            item={item}
            onUpdate={u => updateItem(i, u)}
            onRemove={() => removeItem(i)}
          />
        ))}
      </div>
      <button
        onClick={addItem}
        className="mt-2 w-full py-2 text-xs font-medium text-indigo-500 hover:text-indigo-600 border border-dashed border-indigo-200 hover:border-indigo-400 rounded-xl transition-all"
      >
        + Add option
      </button>
    </div>
  )
}

// ─── Step card ─────────────────────────────────────────────────────────────────

interface StepCardProps {
  step: LeadworkflowStep
  index: number
  total: number
  isExpanded: boolean
  onToggle: () => void
  onUpdate: (updated: LeadworkflowStep) => void
  onRemove: () => void
  onMoveUp: () => void
  onMoveDown: () => void
}

const StepCard: FC<StepCardProps> = ({
  step,
  index,
  total,
  isExpanded,
  onToggle,
  onUpdate,
  onRemove,
  onMoveUp,
  onMoveDown
}) => {
  const meta = STEP_META[step.type] ?? {
    label: step.type,
    color: "bg-gray-50 text-gray-500 border-gray-200"
  }
  const hasItems =
    step.type === "welcome" || step.type === "buttons" || step.type === "list"
  const isWelcome = step.type === "welcome"
  const isSlider = step.type === "slider"
  const isLocation = step.type === "location_autocomplete"

  const set = <K extends keyof LeadworkflowStep>(
    key: K,
    val: LeadworkflowStep[K]
  ) => onUpdate({ ...step, [key]: val })

  return (
    <div
      className={`rounded-2xl border transition-all duration-200 ${
        isExpanded
          ? "border-indigo-200 shadow-md shadow-indigo-50"
          : "border-gray-100 hover:border-gray-200 hover:shadow-sm"
      }`}
    >
      {/* Header row */}
      <div className="flex items-center gap-2">
        <button
          onClick={onToggle}
          className="flex-1 flex items-center gap-3 p-3.5 text-left min-w-0"
        >
          <div className="flex items-center justify-center w-6 h-6 rounded-full bg-gray-100 text-xs text-gray-400 font-semibold shrink-0">
            {index + 1}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-gray-800 truncate">
              {isWelcome ? step.header : step.title || step.id}
            </p>
            <p className="text-xs text-gray-400 font-mono">{step.id}</p>
          </div>
          <span
            className={`text-xs font-medium px-2 py-0.5 rounded-full border shrink-0 ${meta.color}`}
          >
            {meta.label}
          </span>
          <span
            className={`text-gray-400 text-sm transition-transform duration-200 shrink-0 ${
              isExpanded ? "rotate-180" : ""
            }`}
          >
            ▾
          </span>
        </button>
        {/* Move buttons */}
        <div className="flex flex-col pr-2 gap-0.5">
          <button
            disabled={index === 0}
            onClick={onMoveUp}
            className="w-5 h-5 flex items-center justify-center rounded hover:bg-gray-100 text-gray-300 hover:text-gray-600 disabled:opacity-20 disabled:cursor-not-allowed text-xs transition-colors"
          >
            ▲
          </button>
          <button
            disabled={index === total - 1}
            onClick={onMoveDown}
            className="w-5 h-5 flex items-center justify-center rounded hover:bg-gray-100 text-gray-300 hover:text-gray-600 disabled:opacity-20 disabled:cursor-not-allowed text-xs transition-colors"
          >
            ▼
          </button>
        </div>
      </div>

      {/* Expanded editor */}
      {isExpanded && (
        <div className="px-4 pb-4 space-y-4 border-t border-gray-100 pt-4">
          {/* ID + Type */}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Step ID">
              <TextInput
                value={step.id}
                onChange={v => set("id", v)}
                placeholder="step_id"
              />
            </Field>
            <Field label="Type">
              <select
                value={step.type}
                onChange={e =>
                  set("type", e.target.value as LeadworkflowStepType)
                }
                className="w-full px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-md text-gray-700 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
              >
                {STEP_TYPES.map(t => (
                  <option key={t} value={t}>
                    {STEP_META[t].label}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          {/* Welcome: header / subheader / teaser */}
          {isWelcome && (
            <>
              <Field label="Header">
                <TextInput
                  value={step.header ?? ""}
                  onChange={v => set("header", v)}
                  placeholder="Main headline"
                />
              </Field>
              <Field label="Sub-header">
                <TextInput
                  value={step.subheader ?? ""}
                  onChange={v => set("subheader", v)}
                  placeholder="Supporting text"
                />
              </Field>
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                <div>
                  <p className="text-sm font-medium text-gray-700">
                    Teaser rate
                  </p>
                  <p className="text-xs text-gray-400">
                    Show a teaser rate on this step
                  </p>
                </div>
                <BBSwitch
                  enabled={step.teaser_rate_enabled === true}
                  setEnabled={v => set("teaser_rate_enabled", v)}
                />
              </div>
            </>
          )}

          {/* Non-welcome title + title_icon */}
          {!isWelcome && (
            <>
              <Field label="Question / Title">
                <TextInput
                  value={step.title ?? ""}
                  onChange={v => set("title", v)}
                  placeholder="Question shown to user"
                />
              </Field>
              <Field label="Title icon URL">
                <TextInput
                  value={step.title_icon ?? ""}
                  onChange={v => set("title_icon", v || undefined)}
                  placeholder="https://cdn.bankingbridge.com/... (optional)"
                />
              </Field>
            </>
          )}

          {/* Location: placeholder */}
          {isLocation && (
            <Field label="Placeholder">
              <TextInput
                value={step.placeholder ?? ""}
                onChange={v => set("placeholder", v)}
                placeholder="Enter City, State, Zip, etc..."
              />
            </Field>
          )}

          {/* Slider fields */}
          {isSlider && (
            <div className="grid grid-cols-2 gap-3">
              <NumberInput
                label="Range start"
                value={step.range_start}
                onChange={v => set("range_start", v)}
              />
              <NumberInput
                label="Range end"
                value={step.range_end}
                onChange={v => set("range_end", v)}
              />
              <NumberInput
                label="Step size"
                value={step.step}
                onChange={v => set("step", v)}
              />
              <NumberInput
                label="Initial"
                value={step.initial}
                onChange={v => set("initial", v)}
                placeholder="optional"
              />
              <NumberInput
                label="Point size"
                value={step.point_size}
                onChange={v => set("point_size", v)}
                placeholder="optional"
              />
              <Field label="Prefix">
                <TextInput
                  value={step.prefix ?? ""}
                  onChange={v => set("prefix", v || undefined)}
                  placeholder="$ or %"
                />
              </Field>
            </div>
          )}

          {/* Items editor */}
          {hasItems && (
            <ItemsEditor
              items={step.items ?? []}
              onChange={items => set("items", items)}
            />
          )}

          {/* Remove */}
          <div className="flex justify-end pt-1">
            <button
              onClick={onRemove}
              className="text-xs text-red-400 hover:text-red-500 font-medium transition-colors"
            >
              Remove step
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Main component ────────────────────────────────────────────────────────────

const LeadworkflowSteps: FC<Props> = ({ values, setConfigField }) => {
  const [expandedIdx, setExpandedIdx] = useState<number | null>(null)
  const steps: LeadworkflowStep[] = values.config_json.steps ?? []

  const setSteps = (next: LeadworkflowStep[]) => setConfigField("steps", next)

  const updateStep = (i: number, updated: LeadworkflowStep) => {
    const next = [...steps]
    next[i] = updated
    setSteps(next)
  }

  const removeStep = (i: number) => {
    setSteps(steps.filter((_, idx) => idx !== i))
    if (expandedIdx === i) setExpandedIdx(null)
    else if (expandedIdx !== null && expandedIdx > i)
      setExpandedIdx(expandedIdx - 1)
  }

  const moveStep = (i: number, dir: -1 | 1) => {
    const next = [...steps]
    const j = i + dir
    if (j < 0 || j >= next.length) return
    ;[next[i], next[j]] = [next[j], next[i]]
    setSteps(next)
    setExpandedIdx(j)
  }

  const addStep = (type: LeadworkflowStepType) => {
    const newStep = getLeadworkflowStep(type)
    setSteps([...steps, newStep])
    setExpandedIdx(steps.length)
  }

  const addButtons = (
    <div className="flex md:flex-col gap-1.5 items-center md:items-start">
      <p className="text-sm text-gray-400 md:mb-1 mr-2 md:mr-0">Add step:</p>
      {STEP_TYPES.map(type => (
        <button
          key={type}
          onClick={() => addStep(type)}
          className={`flex justify-center items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full border transition-all hover:shadow-sm ${STEP_META[type].color} border-current/20 md:min-w-24`}
        >
          + {STEP_META[type].label}
        </button>
      ))}
    </div>
  )

  return (
    <div className="pt-2">
      {/* Mobile: add buttons on top */}
      <div className="md:hidden mb-4">{addButtons}</div>

      <div className="flex gap-6">
        {/* Steps list */}
        <div className="flex-1 space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold text-gray-700">Flow steps</p>
            <span className="text-xs text-gray-400">{steps.length} steps</span>
          </div>

          <div className="space-y-2">
            {steps.map((step, i) => (
              <StepCard
                key={`${step.id}-${i}`}
                step={step}
                index={i}
                total={steps.length}
                isExpanded={expandedIdx === i}
                onToggle={() => setExpandedIdx(expandedIdx === i ? null : i)}
                onUpdate={updated => updateStep(i, updated)}
                onRemove={() => removeStep(i)}
                onMoveUp={() => moveStep(i, -1)}
                onMoveDown={() => moveStep(i, 1)}
              />
            ))}
          </div>
        </div>

        {/* Desktop: add buttons stacked on the right */}
        <div className="hidden md:block shrink-0 pt-6">{addButtons}</div>
      </div>
    </div>
  )
}

export default LeadworkflowSteps
